import { extname } from "@std/path";
import { getMedia } from "@trydig/storyblok";
import { typeByExtensionSimple } from "@trydig/utils";
import Image from "next/image";
import { useMemo } from "react";
import { Picture } from "@/components/ui/Picture";
import { getLqipProps } from "@/lib/utils";

import type { StoryblokAsset, StoryblokMultiasset } from "@/storyblok";
import styles from "./Asset.module.css";
import { Video } from "./Video";

const getStreamVideoUrl = (url: string) => {
	return `https://streams.frend.dev/video/v1?url=${encodeURIComponent(url)}`;
};

/**
 * Produces media sizes for mobile first
 * @example
 * sizes(100, 50, 25)
 * // 100vw, (min-width: 768px) 50vw, (min-width: 1024px) 25vw
 *
 * @description this is used to produce the sizes attribute for the Image component
 */
export const sizes = (main: number, md?: number, lg?: number) => {
	const sizes: string[] = [];

	if (lg) {
		sizes.push(`(min-width: 1024px) ${lg}vw`);
	}

	if (md) {
		sizes.push(`(min-width: 768px) ${md}vw`);
	}

	sizes.push(`${main}vw`);

	return sizes.join(", ");
};

export interface AssetProps {
	src:
		| string
		| {
				mobile:
					| string
					| {
							src: string;
							objectPosition?: string;
					  };
				desktop:
					| string
					| {
							src: string;
							objectPosition?: string;
					  };
		  };
	sizes?: string;
	alt: string;
	quality?: number;
	autoPlay?: boolean;
	controls?: boolean;
	loop?: boolean;
	muted?: boolean;
	priority?: boolean;
	fetchPriority?: "high" | "auto" | "low";
	placeholder?: "blur";
	blurDataURL?: string;
	getUrl?: (url: string) => string;
	objectPosition?: string | undefined | null;
	lqip?: string | number;
	soundControl?: boolean;
	soundControlLight?: boolean;
}

const AssetRenderer = ({
	src,
	alt,
	sizes,
	autoPlay = true,
	controls,
	loop = true,
	muted = true,
	priority,
	getUrl,
	quality,
	blurDataURL,
	placeholder,
	objectPosition,
	lqip,
	fetchPriority,
	soundControl = true,
	soundControlLight,
	className,
	...rest
}: Omit<AssetProps, "src"> & { src: string; className?: string }) => {
	const url = src;

	const { type } = useMemo(() => {
		const url = new URL(src);
		const ext = extname(url.pathname.toLowerCase());
		const type = typeByExtensionSimple(ext);
		return {
			type,
			ext,
		};
	}, [src]);

	if (!type) {
		console.error("No type found for", src);
		return null;
	}

	if (type === "image") {
		// eslint-disable-next-line turbo/no-undeclared-env-vars
		if (!sizes && process.env.NODE_ENV === "development") {
			console.error(
				`No sizes provided for image asset ${url}\n please add them.`,
			);
		}

		return (
			<Image
				className={className}
				{...rest}
				{...getLqipProps(lqip, {
					style: {
						objectPosition: objectPosition || undefined,
					},
				})}
				fetchPriority={fetchPriority}
				placeholder={placeholder}
				blurDataURL={blurDataURL}
				quality={quality}
				priority={priority}
				src={getUrl ? getUrl(url) : url}
				alt={alt}
				fill
				sizes={sizes}
			/>
		);
	}

	if (type === "video") {
		return (
			<Video
				src={getStreamVideoUrl(url)}
				className={styles.video}
				wrapClassName={className}
				soundControl={soundControl}
				soundControlLight={soundControlLight}
				{...getLqipProps(lqip, rest)}
				{...{ autoPlay, controls, loop, muted }}
			/>
		);
	}
};

const getSrc = (source: Exclude<AssetProps["src"], string>["mobile"]) => {
	if (typeof source === "string")
		return {
			src: source,
		};
	return source;
};

/**
 * Asset component - uses FILL by default.
 * To use this component you must provide a container with the valid dimensions / aspect-ratio
 */
export const Asset = ({
	src,
	alt,
	sizes,
	autoPlay,
	controls,
	loop,
	muted,
	priority,
	blurDataURL,
	placeholder,
	quality,
	getUrl,
	objectPosition,
	lqip,
	soundControl,
	soundControlLight,
}: AssetProps) => {
	if (typeof src === "object") {
		const { mobile, desktop } = src;
		const mobileSrc = getSrc(mobile);
		const desktopSrc = getSrc(desktop);

		const mobileType = typeByExtensionSimple(extname(mobileSrc.src));
		const desktopType = typeByExtensionSimple(extname(desktopSrc.src));
		if (mobileType === "image" && desktopType === "image") {
			return (
				<Picture
					lqip={lqip}
					media={{
						"(max-width: 768px)": {
							src: desktopSrc.src,
							style: {
								objectPosition: mobileSrc.objectPosition,
							},
						},
						"(min-width: 769px)": {
							src: mobileSrc.src,
							style: {
								objectPosition: desktopSrc.objectPosition,
							},
						},
					}}
					quality={quality}
					alt={alt}
					sizes={sizes || "100vw"}
					loading={priority ? "eager" : "lazy"}
					decoding="async"
					fill
					fetchPriority={priority ? "high" : "auto"}
				/>
			);
		}

		return (
			<>
				<AssetRenderer
					lqip={lqip}
					className={styles.mobile}
					src={mobileSrc.src}
					{...{
						quality,
						alt,
						sizes,
						autoPlay,
						controls,
						loop,
						muted,
						priority,
						blurDataURL,
						placeholder,
						getUrl,
						soundControl,
						soundControlLight,
						objectPosition: mobileSrc.objectPosition,
					}}
					data-viewmode="mobile"
				/>
				<AssetRenderer
					lqip={lqip}
					className={styles.desktop}
					src={desktopSrc.src}
					{...{
						quality,
						alt,
						sizes,
						autoPlay,
						controls,
						loop,
						muted,
						priority,
						blurDataURL,
						placeholder,
						getUrl,
						soundControl,
						soundControlLight,
						objectPosition: desktopSrc.objectPosition,
					}}
					data-viewmode="desktop"
				/>
			</>
		);
	}

	return (
		<AssetRenderer
			src={src}
			{...{
				lqip,
				quality,
				alt,
				sizes,
				autoPlay,
				controls,
				loop,
				muted,
				priority,
				blurDataURL,
				placeholder,
				getUrl,
				soundControl,
				soundControlLight,
				objectPosition,
			}}
		/>
	);
};

export type AssetType =
	| StoryblokAsset
	| {
			mobile: StoryblokAsset;
			desktop?: StoryblokAsset;
	  };

export const AssetBlok = ({
	source: originalSource,
	sizes,
	...rest
}: {
	source: AssetType | StoryblokMultiasset;
	sizes?: string;
} & Omit<AssetProps, "src" | "alt">) => {
	const blok = multiAssetToSingleAsset(originalSource);

	if (!blok) return null;

	if ("mobile" in blok && typeof blok.mobile === "object") {
		const mobile = getMedia(blok.mobile as StoryblokAsset);
		if (!mobile) return null;
		const desktop = blok.desktop
			? getMedia(blok.desktop as StoryblokAsset)
			: undefined;

		if (!desktop)
			return (
				<Asset
					src={mobile.url}
					alt={mobile.alt || ""}
					sizes={sizes}
					{...rest}
					objectPosition={mobile.objectPosition}
				/>
			);
		return (
			<Asset
				alt={mobile.alt || ""}
				sizes={sizes}
				src={{
					mobile: mobile.url,
					desktop: desktop?.url,
				}}
				{...rest}
			/>
		);
	}

	const media = getMedia(blok as StoryblokAsset);
	if (!media) {
		console.error("No media found for", blok);
		return null;
	}

	return (
		<Asset
			src={media.url}
			alt={media.alt || ""}
			sizes={sizes}
			{...rest}
			objectPosition={media.objectPosition}
		/>
	);
};

export const multiAssetToSingleAsset = (
	assets: StoryblokMultiasset | AssetType,
): AssetType => {
	if (Array.isArray(assets)) {
		if (assets.length <= 1) {
			return assets[0]! as StoryblokAsset;
		}
		return {
			mobile: assets[0]! as StoryblokAsset,
			desktop: assets[1]! as StoryblokAsset,
		};
	}

	return assets;
};
